/**
 * Recording a кружочек or a voice message, and encoding it on the way in.
 *
 * The handoff asks for compression before upload: a square H.264 picture for a
 * video note and mono audio for both kinds, at whatever the admin page says —
 * 384×384 at 24fps and 800 kbps over 500 КБ of audio a minute, by default. All
 * of that is done *while capturing* rather than as a pass
 * afterwards, because the only way a browser can re-encode a finished file is to
 * play it back in real time — a minute of waiting to send a minute of video.
 * Encoding at capture costs nothing and produces the same bytes.
 *
 * What that leaves for later is the upload, which is what the sending bubble's
 * progress actually counts. See sendMedia in api/client.ts.
 *
 * A third kind lives here too and is not a кружочек: startClip, the video taken
 * on the camera screen. It shares this file's machinery — the MIME preference,
 * the collector, the microphone and its gain stage — and shares none of the
 * shaping, because what it produces is an ordinary rectangular clip that travels
 * as an album attachment rather than as a circle. Photographs are elsewhere
 * again, in viewfinder.ts, which records nothing.
 *
 * Inside the iOS app one of the three is gone from here: a кружочек is filmed by
 * AVFoundation and this file never sees it. Everything else on this page — voice
 * messages, camera-screen clips, and the whole of the web build including iOS
 * Safari — still runs every line below. startCapture is where the two paths
 * part; lib/nativeVideoNote.ts is the other one, and says why it exists.
 */

import { hasNativeVideoNote } from './nativeCamera.ts';
import { reportClientError } from './errorReporter.ts';
import { clampTunables, type Limit } from './tunables.ts';
import { importQuality, targetBitsPerSecond } from './videoCompress.ts';
import { canJoinSegments, joinSegments, type Segment } from './videoJoin.ts';

export type RecordMode = 'voice' | 'video';

/** Which way the camera points; the names WebRTC itself uses. */
export type Facing = 'user' | 'environment';

/** What a finished take hands back, ready to be posted. */
export interface Recorded {
  mode: RecordMode;
  blob: Blob;
  /** Without the `;codecs=` parameter — what goes into the database. */
  mime: string;
  /** Measured here, because a MediaRecorder file often carries no duration. */
  durationMs: number;
  /** A still from the capture canvas: the circle's frame before play. */
  poster: Blob | null;
}

/**
 * A picture that is somewhere else, and takes a rectangle to say where.
 *
 * The iOS кружочек is drawn by an AVCaptureVideoPreviewLayer sitting above the
 * WebView, so there is nothing for the page to mount — only somewhere for the
 * page to say the circle is. `null` puts the layer away.
 *
 * The take hands this out rather than the component reaching for it, and that is
 * deliberate: RecordPreview then knows nothing about Capacitor, imports nothing
 * from it, and the whole native module stays behind the dynamic import in
 * startCapture where the web bundle can never reach it.
 */
export interface NativePreview {
  setFrame: (rect: { x: number; y: number; width: number; height: number } | null) => void;
}

/**
 * What the composer puts in the circle while a кружочек is being filmed.
 *
 * An element on the web: the take's own, moved into the page rather than copied
 * into it, so that what is framed is literally what is written to the file. A
 * NativePreview on iOS, where the picture is not in the page at all. See
 * RecordPreview, which is where the two part company.
 */
export type Preview = HTMLVideoElement | NativePreview;

export interface Capture {
  readonly mode: RecordMode;
  /**
   * How the camera can be shown, so the composer can show it — video takes only,
   * null throughout a voice one.
   *
   * Handing back the take's own element rather than opening a second camera is
   * the point: the preview is not an approximation of the кружочек, it is the
   * track being written to the file. The circle is a mask over it and the mirror
   * on a self-view is a transform on it, and neither reaches the recording. The
   * native path keeps that property by a different route — same session, same
   * camera, the layer and the writer fed from one AVCaptureSession.
   */
  readonly preview: Preview | null;
  /**
   * Turns the camera round mid-take. Video only; null for a voice one, and null
   * for a кружочек when the flip is switched off at /admin.
   *
   * What it does is cut rather than swap: a MediaRecorder is bound to the tracks
   * it was handed, so the current one is closed, the other camera is opened, and
   * a new one starts. The pieces are stitched back into one file at `stop` — see
   * startVideoNote, and lib/videoJoin.ts for the stitching.
   */
  readonly useCamera: ((facing: Facing) => Promise<void>) | null;
  /**
   * Zooms the camera being filmed. Video only; null for a voice take.
   *
   * It exists because the take owns the camera and nobody else can reach it.
   * The camera screen's pinch drives the viewfinder's own track right up until
   * recording starts — and at that moment the viewfinder has let its camera go
   * and this one has opened its own, so the same fingers have to arrive
   * somewhere else. Without it the pinch simply stops working the instant a
   * кружочек starts, which is the half of the gesture that matters most: the
   * framing a sender corrects is the one they can see going into the file.
   *
   * Best-effort by design, on both sides — a camera that will not go that far
   * goes as far as it can rather than refusing, which is what every camera app
   * does and what the sender's fingers are already asking for.
   */
  readonly setZoom: ((factor: number) => void) | null;
  /**
   * Ends the take and hands back the bytes, releasing camera and microphone.
   *
   * `onJoin` counts the stitching from 0 to 1, and is called at all only for a
   * take that was flipped. It fires once with 0 before any work starts, which is
   * what the record pill uses to decide whether to say «Собираем» — a take of
   * one piece never calls it, and so never flashes it.
   */
  stop: (onJoin?: (fraction: number) => void) => Promise<Recorded>;
  /** Throws the take away. Safe to call twice; always releases the devices. */
  cancel: () => void;
}

/**
 * The hard cap. A кружочек is a moment, not a broadcast — the handoff said a
 * minute, and lived use said the minute kept ending mid-sentence, so it is two.
 * The record ring and the pill count against this number, and at the default
 * bitrates a full take weighs ≈12.4 МБ, still nowhere near the upload cap.
 * Both recorders read it from here: the web one arms its own timer with it and
 * the iOS writer is handed it as `maxMs`, so there is exactly one number to
 * change and this is it.
 */
export const MAX_VIDEO_NOTE_MS = 120_000;

/** What a clip from the camera screen hands back — a file, near enough. */
export interface Clip {
  blob: Blob;
  /** Without the `;codecs=` parameter, like every other blob leaving here. */
  mime: string;
  durationMs: number;
}

export interface ClipTake {
  /**
   * False when the microphone was refused and this take is being recorded
   * silently. Known before the first frame, so the screen can say so while it is
   * still worth saying rather than after the clip is in the album.
   */
  readonly sound: boolean;
  /** Ends the take and hands back the bytes. Does not touch the camera. */
  stop: () => Promise<Clip>;
  /** Throws the take away. Safe to call twice; does not touch the camera. */
  cancel: () => void;
}

/**
 * The ceiling on a clip whatever the upload limit says. Three minutes is well
 * past what anybody films to attach to a message, and short enough that the
 * bytes are still in memory rather than being a problem of their own.
 */
export const MAX_CLIP_MS = 3 * 60_000;

/**
 * How long the camera screen may record before it stops itself.
 *
 * Length is the only handle there is on size while a take is running, and size
 * is what the server refuses. The clip is recorded at the import budget — see
 * startClip — so seconds convert to bytes exactly, and stopping at the limit is
 * better than the two alternatives: a 413 after the fact, or the compose screen
 * refusing a clip that has already been filmed.
 */
export function clipLimitMs(maxUploadBytes: number): number {
  const affordable = (maxUploadBytes * 8 * 1000) / targetBitsPerSecond();
  return Math.max(1000, Math.min(MAX_CLIP_MS, Math.floor(affordable)));
}

/**
 * How good a recording is, as the encoder is configured: what a minute of audio
 * may weigh, and the picture's frame, rate and bitrate.
 *
 * All four are admin settings, delivered with the session and pushed in here at
 * sign-in — they have to be, because the encoding happens on this device while
 * the take is being made. The defaults are the handoff's: a 384px square (the
 * real on-screen size of a кружочек at 2x, so there is no reason to store more)
 * at 24fps and 800 kbps, and 500 КБ of audio a minute.
 *
 * Audio is set as a size per minute rather than a bitrate because that is the
 * number anyone can check against the file that arrives, and because it means
 * the same thing whether it is a voice message or a кружочек's soundtrack. The
 * picture is set the way video is actually configured, since only one of the
 * two kinds has one.
 */
export interface RecordingQuality {
  audioKbPerMinute: number;
  /** How far the microphone is turned up before encoding, as a multiple. */
  audioGain: number;
  /** The side of the square frame, in pixels. */
  videoPx: number;
  videoFps: number;
  videoKbps: number;
  /**
   * Whether a кружочек may change camera at all — the one setting here that is
   * a choice about the feature rather than a number about the encoder.
   *
   * On, the flip button is on the live circle and on the trim screen, and a take
   * that used it is recorded in pieces and stitched before it is sent (see
   * startVideoNote and lib/videoJoin.ts). Off, there is no button anywhere and
   * every кружочек is the front camera — which is what nine out of ten of them
   * are, and it costs nothing on a device where the stitching misbehaves.
   */
  videoFlip: boolean;
}

/** The numeric half, which is the half that has limits. */
type QualityNumbers = Omit<RecordingQuality, 'videoFlip'>;

export const QUALITY_DEFAULTS: RecordingQuality = {
  audioKbPerMinute: 500,
  audioGain: 2.5,
  videoPx: 384,
  videoFps: 24,
  videoKbps: 800,
  videoFlip: true,
};

/** The same guard rails the server applies — see LIMITS in server/src/settings.ts. */
export const QUALITY_LIMITS: Record<keyof QualityNumbers, Limit> = {
  audioKbPerMinute: { min: 400, max: 800 },
  audioGain: { min: 1, max: 6, step: 0.1 },
  videoPx: { min: 256, max: 720 },
  videoFps: { min: 12, max: 30 },
  videoKbps: { min: 200, max: 2000 },
};

let quality: RecordingQuality = { ...QUALITY_DEFAULTS };

export function recordingQuality(): RecordingQuality {
  return quality;
}

/**
 * Clamped here as well as on the server: these are the values the encoder is
 * handed, and a recording is not the place to find out that a stored setting was
 * nonsense. See clampTunables for the rest of why, and for the rule the import
 * settings are held to by the same function.
 */
export function setRecordingQuality(next: Partial<RecordingQuality>): void {
  quality = {
    ...clampTunables<QualityNumbers>(quality, next, QUALITY_LIMITS),
    // Outside the clamp because a boolean cannot be out of range, and left
    // alone when it is missing for the reason clampTunables leaves numbers
    // alone: a server older than this knob must not switch the flip off.
    videoFlip: typeof next.videoFlip === 'boolean' ? next.videoFlip : quality.videoFlip,
  };
}

/**
 * Whether the camera may be turned round at all — see RecordingQuality.videoFlip.
 *
 * Two answers to one question, because the two recorders pay for a flip in
 * completely different currencies. In here it cuts the take into pieces that
 * have to be stitched afterwards, so it is offered only where the stitching is
 * possible — a browser that cannot do it would be offering a button that costs
 * the кружочек. Inside the iOS app there is nothing to stitch: the take belongs
 * to an AVCaptureSession rather than to a track, so the session swaps its own
 * camera and one AVAssetWriter goes on writing one movie across it (see
 * useCamera in VideoNoteRecorder.swift). The admin switch governs both.
 *
 * This used to be `false` on iOS full stop, back when the native recorder had
 * only just replaced MediaRecorder and the freeze it was there to fix had not
 * yet stayed fixed for long enough to build anything on top of.
 */
export function canFlipCamera(): boolean {
  if (!quality.videoFlip) return false;
  return hasNativeVideoNote || canJoinSegments();
}

/** КБ a minute as the bits a second MediaRecorder wants. 500 КБ → ≈68 kbps. */
export function audioBitsPerSecond(): number {
  return Math.round((quality.audioKbPerMinute * 1024 * 8) / 60);
}

/** kbps as the bits a second MediaRecorder wants. */
export function videoBitsPerSecond(): number {
  return quality.videoKbps * 1000;
}

/**
 * How long recording carries on after the finger lifts.
 *
 * A word ends and the thumb comes off it, in that order, but only just — and
 * stopping the encoder in the same instant clips the tail of whatever was being
 * said. Three tenths of a second is long enough to catch the end of a sentence
 * and short enough that nobody notices the send taking it.
 */
const TAIL_MS = 300;

/**
 * H.264 + AAC first, which is what the handoff specifies and what every phone
 * can play without transcoding. WebM is the fallback for browsers whose
 * MediaRecorder cannot write MP4 — the file is bigger and, more awkwardly,
 * often carries no seek index, so a trimmed range may start from the beginning
 * instead of the in-point. The bubbles stop at the out-point either way.
 */
const VIDEO_MIMES = [
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
  'video/mp4',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

/** Opus first, as the handoff asks; audio/mp4 is Safari's only offer. */
const VOICE_MIMES = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4', 'audio/webm'];

/** True when this browser can record at all — the record button hides if not. */
export function canRecord(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    navigator.mediaDevices?.getUserMedia !== undefined &&
    pickMime(VOICE_MIMES) !== null
  );
}

function pickMime(candidates: string[]): string | null {
  return candidates.find((mime) => MediaRecorder.isTypeSupported(mime)) ?? null;
}

/** `video/webm;codecs=vp8,opus` → `video/webm`, which is what the server stores. */
export function baseMime(mime: string): string {
  return mime.split(';')[0]!.trim().toLowerCase();
}

/** `.mp4`, `.webm` — only ever a hint; the server names the file on disk. */
function extensionFor(mime: string): string {
  const subtype = baseMime(mime).split('/')[1] ?? 'bin';
  return subtype === 'mpeg' ? 'mp3' : subtype;
}

export function fileNameFor(mode: RecordMode, mime: string): string {
  return `${mode === 'video' ? 'note' : 'voice'}.${extensionFor(mime)}`;
}

/**
 * `video-20260807-142530.mp4` — a clip named after the moment it was filmed.
 *
 * Dated rather than fixed, unlike the two above: a кружочек is a message and a
 * clip is an attachment, and it sits in the album beside photographs that are
 * named this way already (see photoFileName in viewfinder.ts). Only ever a hint;
 * the server names what it stores.
 */
export function clipFileName(mime: string, at: Date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  const date = `${at.getFullYear()}${pad(at.getMonth() + 1)}${pad(at.getDate())}`;
  const time = `${pad(at.getHours())}${pad(at.getMinutes())}${pad(at.getSeconds())}`;
  return `video-${date}-${time}.${extensionFor(mime)}`;
}

/**
 * Whichever way the camera pointed last. A preference rather than state, and
 * written down rather than held: the camera someone turned round is the camera
 * they will want next time, and «next time» is usually a fresh page — a
 * preference that lived in a module variable was forgotten by every reload.
 * The storage is the variable: read on use, so there is no cached copy to
 * fall out of step with it.
 */
const FACING_KEY = 'chat.camera.lastFacing';

/** For the browser with no storage at all: the choice still holds until reload. */
let fallbackFacing: Facing = 'user';

/** The raw memory, no gate — see cameraFacing for who must not read it raw. */
export function lastFacing(): Facing {
  try {
    return localStorage.getItem(FACING_KEY) === 'environment' ? 'environment' : 'user';
  } catch {
    return fallbackFacing;
  }
}

/**
 * With the flip switched off this is not a preference but a fact: there is no
 * button anywhere that could have set it to anything else, and a stale
 * `environment` from before the switch was thrown would open the back camera on
 * a screen with no way to get back. The camera screen reads lastFacing instead,
 * because its own flip button answers to a different gate — hasSecondCamera —
 * and a way back always exists there.
 */
export function cameraFacing(): Facing {
  return canFlipCamera() ? lastFacing() : 'user';
}

export function flipCamera(): Facing {
  const next = lastFacing() === 'user' ? 'environment' : 'user';
  fallbackFacing = next;
  try {
    localStorage.setItem(FACING_KEY, next);
  } catch {
    // A browser without storage forgets the choice on reload, and loses
    // nothing else — the flip itself still happens, and holds until then.
  }
  return next;
}

/** Everything a take holds open, so one call can let all of it go. */
interface Devices {
  tracks: MediaStreamTrack[];
  video?: HTMLVideoElement;
  /** Tears down the gain graph, if there is one. See amplify. */
  audio?: () => void;
}

function release(devices: Devices): void {
  devices.audio?.();
  for (const track of devices.tracks) track.stop();
  if (devices.video) {
    // Cleared as well as removed, and not only to let go of the stream: an
    // element with no source is how RecordPreview tells a take that has ended
    // from one that is merely not on screen, so that it does not put a dead
    // element back into the page. See the effect there.
    devices.video.srcObject = null;
    devices.video.remove();
  }
}

/**
 * Wires a recorder up to keep its own output, and hands back the way to ask for
 * it. Call this before `start()`; call the result to end the take.
 *
 * Both halves have to be separate, and this is the reason. `start()` is called
 * without a timeslice, so the whole recording arrives in a single
 * `dataavailable` — and when the length cap stops the recorder by itself, that
 * event is already queued before anything asks for the bytes. A handler attached
 * at stop time can therefore miss it, and what it misses is not the last word but
 * the entire file. Attached at construction, it cannot.
 *
 * `stop()` is also not the end of a MediaRecorder: the final chunk arrives
 * afterwards, so the blob is taken on `onstop` and not a moment earlier.
 */
function collector(recorder: MediaRecorder, mime: string): () => Promise<Blob> {
  const chunks: Blob[] = [];
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  };

  const finished = new Promise<void>((resolve) => {
    recorder.onstop = () => resolve();
  });

  // The blob carries the base type, not the recorder's full `mimeType`.
  //
  // Chrome hands back `video/mp4;codecs=avc1.42E01E,mp4a.40.2`, and that comma
  // is not a legal character in an unquoted HTTP parameter value. The multipart
  // parser on the other end therefore throws the whole Content-Type away and
  // falls back to text/plain, and the upload arrives looking like a document.
  // Nothing downstream reads the codec list, so the container type is what
  // travels.
  const type = baseMime(mime);

  return async () => {
    if (recorder.state !== 'inactive') recorder.stop();
    await finished;
    return new Blob(chunks, { type });
  };
}

/**
 * The size a кружочек is asked for, which is now the size it is recorded at —
 * there is no canvas downscaling it afterwards. See startVideoNote.
 *
 * A square is asked for and will not be given: phone cameras offer a handful of
 * fixed modes and none of them is square, so what arrives is the nearest one and
 * the circle is cut out of it in CSS. The floor of 480 is what steers that
 * choice. A camera handed 384 picks 352×288 on iOS — measurably softer than the
 * 384 the circle is drawn at — while 480 lands on 640×480, which has a little in
 * hand on the short side and does not cost anything: the file's weight is the
 * bitrate, and that is set separately.
 */
function cameraConstraints(facing: Facing): MediaTrackConstraints {
  const side = Math.max(480, quality.videoPx);
  return {
    facingMode: facing,
    width: { ideal: side },
    height: { ideal: side },
    // The rate is asked of the camera now that no timer is pacing a canvas. A
    // camera that ignores it costs bytes rather than correctness, since the
    // bitrate is what the encoder is actually held to.
    frameRate: { ideal: quality.videoFps },
  };
}

/**
 * What the microphone is asked for by everything that records, as opposed to
 * what a call asks for.
 *
 * Echo cancellation is off, and that is the single biggest thing here. Asking for
 * it routes the microphone through the browser's call processor, which mixes down
 * to a narrow band and gates anything it takes for room noise — a voice message
 * comes out sounding like a phone call from 2006. There is nothing to cancel in a
 * recording: no far end is playing, so all the processing can do is take away.
 *
 * Noise suppression and gain control stay on. Those are the two that make a
 * recording made at arm's length in a kitchen intelligible, and neither costs
 * bandwidth.
 *
 * Asking for gain control is not the same as getting it, though, and that is
 * what made every recording quiet: a constraint a browser does not implement is
 * ignored rather than refused, and the platforms that do implement it put it in
 * the same chain as the echo canceller switched off above. The gain is applied
 * explicitly instead — see amplify, which is what actually reaches the encoder.
 */
const MICROPHONE: MediaTrackConstraints = {
  channelCount: 1,
  echoCancellation: false,
  noiseSuppression: true,
  autoGainControl: true,
};

export function openMicrophone(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({ audio: MICROPHONE });
}

/**
 * The microphone, turned up, with a limiter behind it.
 *
 * This is the gain the browser stopped applying when the echo canceller was
 * switched off. Every platform bundles automatic gain control into the same
 * voice-processing chain as the echo canceller — one audio unit on iOS, one
 * processing module in Chrome — so asking for `echoCancellation: false` to keep
 * the full band (see openMicrophone) also gave up the AGC that was making
 * anybody audible. `autoGainControl: true` is asked for and is not enough: a
 * constraint that a browser does not implement is silently ignored rather than
 * refused, and Safari does not implement that one at all. What reached the
 * encoder was the bare microphone, which is quiet, and quiet on every device —
 * which is exactly how it was reported.
 *
 * So the gain goes back explicitly, at the context's own sample rate. That last
 * part is the whole difference from the graph this app used to have and threw
 * away: that one was `new AudioContext({ sampleRate: 16000 })`, and what made
 * recordings sound muffled was the resampling, not the graph. Nothing here
 * touches the bandwidth.
 *
 * The limiter after the gain is what makes raising it safe. Gain alone would
 * clip whoever holds the phone close; a compressor with a fast attack catches
 * the peaks, so the quiet half of the range comes up and the loud half stays
 * where it is.
 *
 * Returns the track to encode. Every failure falls back to the microphone as it
 * came: a recording that is quiet is worth having, and one that never happened
 * because an AudioContext would not start is not.
 *
 * Awaited rather than fired and forgotten, and that is not a detail. A context
 * starts suspended whenever the page is not free to make noise, and a suspended
 * one renders silence rather than raising anything — this app has already lost a
 * recording's opening to exactly that, back when the graph was doing the
 * resampling. Nothing starts encoding until the graph is running.
 */
async function amplify(microphone: MediaStream): Promise<{
  /** `undefined` only for a stream with no audio in it, which cannot happen here. */
  track: MediaStreamTrack | undefined;
  close: () => void;
}> {
  const raw = microphone.getAudioTracks()[0];
  const Context =
    window.AudioContext ??
    (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  // A gain of exactly 1 is the microphone as it came, so there is nothing for a
  // graph to do but add a failure mode — the knob's floor is what turns it off.
  if (!raw || !Context || quality.audioGain <= 1) {
    return { track: raw, close: () => {} };
  }

  try {
    const context = new Context();
    // Before anything is wired up, so a context that cannot start is a fallback
    // to the bare microphone rather than a graph quietly rendering nothing.
    await context.resume();

    const source = context.createMediaStreamSource(microphone);
    const gain = context.createGain();
    gain.gain.value = quality.audioGain;

    const limiter = context.createDynamicsCompressor();
    // Just under full scale, hit hard and let go quickly: this is a safety net
    // for peaks rather than a compressor anyone should be able to hear.
    limiter.threshold.value = -6;
    limiter.knee.value = 3;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.2;

    const destination = context.createMediaStreamDestination();
    // Mono out, because mono is what went in. A destination node defaults to
    // two channels and upmixes to fill them, so every recording that went
    // through this graph was stereo — twice the samples for one microphone,
    // against a MICROPHONE constraint that asks for one channel and an audio
    // allowance that is quoted per minute of it. It surfaced from the other
    // end: the кружочек join reported «2ch 48000Hz» for a track nothing had
    // asked to be stereo.
    destination.channelCount = 1;
    destination.channelCountMode = 'explicit';
    destination.channelInterpretation = 'speakers';
    source.connect(gain).connect(limiter).connect(destination);

    const track = destination.stream.getAudioTracks()[0];
    if (!track) throw new Error('no processed track');

    return {
      track,
      close: () => {
        // The graph holds the microphone open; closing the context is what lets
        // go of it, and the raw track is stopped by release() as it always was.
        source.disconnect();
        void context.close().catch(() => {});
      },
    };
  } catch (error) {
    console.warn('capture: recording at microphone level, gain stage failed', error);
    return { track: raw, close: () => {} };
  }
}

/**
 * Resolves once the element has painted a frame from whatever was last assigned
 * to it — not merely once it agreed to play.
 *
 * The difference is the half-second the camera takes to deliver its first frame,
 * and during it a video element goes on showing the *previous* camera's last
 * frame. Anything that changes with the camera — the self-view mirror, above
 * all — has to wait for this rather than for `play()`, or it lands on the old
 * picture and the swap reads as two separate glitches instead of one cut. The
 * camera screen is what flips mid-stream now that a кружочек cannot; see the
 * flip in CameraScreen.tsx.
 *
 * `requestVideoFrameCallback` is the only way to ask, and Firefox does not have
 * it; there the wait is skipped rather than faked, which is the behaviour this
 * replaces. The timeout is for the camera that opens and then delivers nothing:
 * a mirror one frame late is a blemish, and a flip button that never comes back
 * is a broken screen.
 */
export function firstFrame(video: HTMLVideoElement, timeoutMs = 1000): Promise<void> {
  const element = video as HTMLVideoElement & {
    requestVideoFrameCallback?: (callback: () => void) => number;
  };
  if (!element.requestVideoFrameCallback) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      window.clearTimeout(timer);
      resolve();
    };
    const timer = window.setTimeout(resolve, timeoutMs);
    element.requestVideoFrameCallback!(done);
  });
}

/**
 * Camera and microphone at once, or neither — in one request, which is the part
 * that matters.
 *
 * A browser raises one permission prompt per getUserMedia call, not one per
 * device: asking for both in a single call is a single «разрешить камеру и
 * микрофон?», and asking twice is two dialogs in a row before a кружочек that
 * was meant to start when the finger came down. This used to open the two
 * separately, for a reason that no longer applies — the two streams are still
 * wanted (see startVideoNote, which replaces the camera mid-take and must not
 * touch the audio), but they are cut out of one grant rather than asked for
 * apart. Splitting the tracks afterwards costs nothing; each MediaStream here is
 * a view onto tracks the caller owns and stops.
 *
 * A combined request is also all-or-nothing for free: a device that fails takes
 * the whole call down with it, so there is nothing left open with its indicator
 * light on for a take that is never going to happen.
 */
async function openBoth(facing: Facing): Promise<[MediaStream, MediaStream]> {
  const both = await navigator.mediaDevices.getUserMedia({
    video: cameraConstraints(facing),
    audio: MICROPHONE,
  });
  return [new MediaStream(both.getVideoTracks()), new MediaStream(both.getAudioTracks())];
}

/** The grace period at the end of a take — see TAIL_MS. */
function tail(recorder: MediaRecorder): Promise<void> {
  // Nothing to wait for if the recorder already stopped itself, which is what
  // the length cap does.
  if (recorder.state === 'inactive') return Promise.resolve();
  return new Promise((resolve) => window.setTimeout(resolve, TAIL_MS));
}

/**
 * A кружочек: the camera as it comes, at the size it was asked for and the
 * bitrate the admin page set.
 *
 * What MediaRecorder is handed is the camera's own track. That is the whole
 * shape of this function and the reason it is as short as it is — it used to
 * record `canvas.captureStream()` instead, drawing the centre square of the
 * camera into a 384×384 canvas two dozen times a second so that the crop and the
 * downscale happened at capture. On iOS that canvas track dies partway through a
 * take. The audio carries on to the end, so what arrives is a circle that plays,
 * freezes, and goes on talking over the still: three of nine takes on the
 * reporting phone, cut at 13, 14.5 and 16 seconds of 30 to 40.
 *
 * Nothing above the track can fix that, which is worth stating because the
 * obvious repair was tried first. The draw loop was proved to be still running
 * at the instant the frames stopped — a dead-steady 35ms to the last one, and in
 * one file five held frames of exactly 382 bytes each written after the camera
 * had stalled, so the canvas was being painted and the encoder was ignoring it.
 * A camera track recorded directly, which is what startClip has always done, has
 * not lost a frame on the same phone.
 *
 * So the circle is cut out of a rectangle in CSS instead. That costs less than
 * it sounds: the bubble and the trim screen were doing exactly that anyway,
 * because a circle is a circle and a video is never one, and `cameraConstraints`
 * is what keeps the rectangle small enough to be worth sending.
 *
 * What the canvas took with it, and what this got back a different way, is
 * turning the camera round mid-take. A MediaRecorder is bound to the tracks it
 * was handed, so a flip cuts instead of swapping: the recorder is closed, the
 * other camera is opened, a new one starts, and the pieces are stitched into one
 * MP4 at `stop` (see lib/videoJoin.ts). The microphone is untouched throughout —
 * it belongs to the take, not to the segment — so a flip costs one permission
 * dialog fewer than it looks like it should, and about half a second of picture
 * and sound while the phone lets go of one camera and opens the other. That is a
 * cut, which is what a flip looks like in every app that has one.
 *
 * A take of one piece is not stitched at all: the blob MediaRecorder wrote is
 * the file, exactly as before, and the common case pays nothing for the feature.
 *
 * `onLimit` fires when the two-minute cap stops the recording on its own; the
 * caller still has to call `stop()` to collect the bytes.
 */
export async function startVideoNote(onLimit?: () => void, zoom?: number): Promise<Capture> {
  const mime = pickMime(VIDEO_MIMES);
  if (!mime) throw new Error('Браузер не умеет записывать видео');

  const devices: Devices = { tracks: [] };
  try {
    // Both devices out of one grant — see openBoth — and split again here,
    // because only one of the two goes through a gain stage on the way to the
    // encoder.
    const [camera, microphone] = await openBoth(cameraFacing());
    const opened = camera.getVideoTracks()[0];
    if (!opened) throw new Error('Камера не дала картинку');
    /**
     * How far in the camera is zoomed, as the page last asked for it.
     *
     * The framing the camera screen's pinch chose has to be carried onto this
     * take's own camera — a different track from the viewfinder the zoom was
     * set on, so without it the recording would open at 1× under a circle that
     * was framed closer — and then carried again onto whatever camera a flip
     * puts in its place, which comes up at its own default. Best-effort, like
     * every constraint not in the spec: a camera that refuses films the wide
     * shot rather than nothing.
     */
    let zoomFactor = zoom;
    const zoomCamera = (track: MediaStreamTrack) => {
      if (zoomFactor === undefined) return;
      const constraints = { advanced: [{ zoom: zoomFactor }] } as unknown as MediaTrackConstraints;
      void track.applyConstraints(constraints).catch(() => {});
    };
    zoomCamera(opened);
    /** The camera being filmed right now — replaced, not swapped, by a flip. */
    let cameraTrack: MediaStreamTrack = opened;
    devices.tracks.push(...camera.getTracks());
    devices.tracks.push(...microphone.getTracks());

    /**
     * What the sender watches while recording, and what the poster is taken from
     * at the end. Attached to the document from the start, invisibly: Safari
     * will not decode an element that is not in it, and the composer's circle
     * does not exist yet. RecordPreview moves it in and clears these styles;
     * see the effect there, which puts both back when the circle goes.
     *
     * It outlives a flip: the element is the one the circle has already been
     * handed, so the new camera is assigned to it rather than replacing it.
     */
    const video = document.createElement('video');
    video.srcObject = camera;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('aria-hidden', 'true');
    video.style.cssText =
      'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;left:-1px;top:-1px';
    document.body.appendChild(video);
    devices.video = video;
    await video.play();

    // The turned-up track rather than the microphone's own — a кружочек's
    // soundtrack is a recording like any other, and was just as quiet. Built
    // once for the whole take: every segment records this same track.
    const amplified = await amplify(microphone);
    devices.audio = amplified.close;

    /** The pieces already filmed. A take that is never flipped ends with one. */
    const segments: Segment[] = [];
    /** How much of the allowance the finished pieces have already spent. */
    let banked = 0;

    let recorder: MediaRecorder;
    let collect: () => Promise<Blob>;
    let startedAt = 0;
    let limit = 0;

    /** Starts recording this camera, and arms the cap with what is left of it. */
    const begin = (track: MediaStreamTrack) => {
      recorder = new MediaRecorder(
        new MediaStream(amplified.track ? [track, amplified.track] : [track]),
        {
          mimeType: mime,
          videoBitsPerSecond: videoBitsPerSecond(),
          audioBitsPerSecond: audioBitsPerSecond(),
        },
      );
      collect = collector(recorder, mime);
      startedAt = performance.now();
      recorder.start();
      // The cap is the take's, not the segment's: what is left of the allowance
      // after everything already filmed.
      limit = window.setTimeout(() => {
        if (recorder.state !== 'inactive') recorder.stop();
        onLimit?.();
      }, Math.max(0, MAX_VIDEO_NOTE_MS - banked));
    };

    /** Closes the running segment and puts its bytes away. */
    const bank = async () => {
      window.clearTimeout(limit);
      const elapsed = performance.now() - startedAt;
      segments.push({ blob: await collect(), durationMs: Math.round(elapsed) });
      banked += elapsed;
    };

    begin(cameraTrack);

    return {
      mode: 'video',
      preview: video,
      setZoom: (factor: number) => {
        zoomFactor = factor;
        zoomCamera(cameraTrack);
      },
      useCamera: !canFlipCamera()
        ? null
        : async (facing: Facing) => {
            // No `tail` here, unlike at the end of a take: the 300ms grace
            // exists to catch the end of a sentence somebody is still saying,
            // and mid-flip it would only lengthen the gap between the pieces.
            await bank();

            // Let go before asking: a phone that will not hold two cameras at
            // once refuses the second getUserMedia while the first is open.
            cameraTrack.stop();
            devices.tracks = devices.tracks.filter((track) => track !== cameraTrack);

            const open = async (which: Facing) => {
              const stream = await navigator.mediaDevices.getUserMedia({
                // Video alone. The microphone is already open and recording;
                // asking for it again would be a second permission dialog in
                // the middle of a take.
                video: cameraConstraints(which),
                audio: false,
              });
              const track = stream.getVideoTracks()[0];
              if (!track) throw new Error('Камера не дала картинку');
              return track;
            };

            let next: MediaStreamTrack;
            try {
              next = await open(facing);
            } catch (error) {
              // The other camera would not open. Going back to the one that was
              // working keeps the take alive — it costs a cut, which has already
              // been paid — and the caller puts the button back where it was.
              try {
                next = await open(cameraFacing());
              } catch {
                // Neither camera. There is nothing left to film with, and the
                // pieces already banked are all this take will ever have.
                release(devices);
                throw error;
              }
              cameraTrack = next;
              devices.tracks.push(next);
              video.srcObject = new MediaStream([next]);
              zoomCamera(next);
              await video.play();
              await firstFrame(video);
              begin(next);
              throw error;
            }

            cameraTrack = next;
            devices.tracks.push(next);
            video.srcObject = new MediaStream([next]);
            zoomCamera(next);
            await video.play();
            // Awaited so that the caller turns the mirror over at the moment the
            // new picture appears rather than half a second before it — see the
            // flip in useRecorder.ts, and firstFrame for what it is waiting on.
            await firstFrame(video);
            begin(next);
          },
      async stop(onJoin) {
        // Before the tail rather than inside bank, which is where it is cleared
        // for a flip: the cap firing during those 300ms would call onLimit and
        // start a second ending on top of this one.
        window.clearTimeout(limit);
        await tail(recorder);
        await bank();
        // Ahead of release, which stops the camera and takes the element the
        // frame is being read off with it.
        const poster = await posterFrom(video);
        release(devices);

        const only = segments.length === 1 ? segments[0]! : null;
        const blob = only ? only.blob : await stitch(segments, onJoin);

        return {
          mode: 'video',
          blob,
          // The join always writes MP4, whatever MediaRecorder was writing.
          mime: only ? baseMime(mime) : 'video/mp4',
          durationMs: Math.min(Math.round(banked), MAX_VIDEO_NOTE_MS),
          poster,
        };
      },
      cancel() {
        window.clearTimeout(limit);
        if (recorder.state !== 'inactive') recorder.stop();
        release(devices);
      },
    };
  } catch (error) {
    release(devices);
    throw error;
  }
}

/**
 * The pieces of a flipped take as one file, or a sentence the composer can show.
 *
 * The only place in the client that turns a failed encode into a lost take, and
 * it is deliberate: compressVideo can always fall back to the original file
 * because there is one, and here the alternative would be choosing on the
 * sender's behalf which half of what they filmed to throw away.
 *
 * Which is why the failure says what it was, on the screen, in the words the
 * encoder used. Every other error in this file is a sentence about the device
 * and hides its cause on purpose; this one may not. It broke on every flipped
 * take on one platform and on none of the takes on the other, and the three
 * things that would have identified it in a second — which step refused, what
 * the pieces weighed, whether there were two of them at all — were behind a
 * `console.warn` on a phone, where nobody reads anything. A generic apology on
 * an error that loses what somebody just filmed is the wrong trade.
 *
 * The same note is filed to `client_errors` as well, so it is still countable
 * on /admin afterwards rather than only readable in the moment. See
 * errorReporter.ts, which never throws.
 */
async function stitch(segments: Segment[], onJoin?: (fraction: number) => void): Promise<Blob> {
  try {
    return await joinSegments(
      segments,
      { videoBitsPerSecond: videoBitsPerSecond(), audioBitsPerSecond: audioBitsPerSecond() },
      onJoin,
    );
  } catch (error) {
    // The name as well as the message when it carries anything — a DOMException
    // says what kind it is there and nowhere else, and «NotSupportedError» is
    // half the answer on its own.
    const named = error instanceof Error && error.name && error.name !== 'Error';
    const why = error instanceof Error ? `${named ? `${error.name}: ` : ''}${error.message}` : String(error);
    // What was being joined, which is the other half: a piece that came out
    // empty or a take that somehow has only one are both visible here and
    // invisible in the message.
    const shape = segments
      .map(
        (segment) =>
          `${Math.round(segment.durationMs / 100) / 10}с/${Math.round(segment.blob.size / 1024)}КБ`,
      )
      .join(' + ');

    console.warn('capture: не удалось собрать кружочек из частей', error);
    reportClientError({ kind: 'crash', detail: `videoJoin ${shape}`, message: why });
    throw new Error(`Не удалось собрать видео: ${why.slice(0, 160)} · части: ${shape}`);
  }
}

/**
 * The frame the circle shows before anyone plays it, taken off the element the
 * sender was watching a moment ago.
 *
 * Unmirrored, whatever the preview was doing: a self-view is flipped in CSS so
 * that framing works the way a mirror does, and what travels is the picture the
 * camera saw. Same rule and the same reason as `snapshot` in viewfinder.ts,
 * which takes a photograph off the same kind of element.
 *
 * Every way this can fail ends in a circle with no poster rather than in a lost
 * take — the bytes are collected before it runs, and the bubble has a frame of
 * the video to fall back on.
 */
function posterFrom(video: HTMLVideoElement): Promise<Blob | null> {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (width === 0 || height === 0) return Promise.resolve(null);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return Promise.resolve(null);
  context.drawImage(video, 0, 0);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.82);
  });
}

/**
 * A voice message: mono Opus at whatever a minute is allowed to weigh.
 *
 * The microphone goes to the encoder as it comes, at its own sample rate. It used
 * to be pulled through a 16 kHz AudioContext first, which is where the muffled
 * quality came from — 16 kHz cuts everything above 8 kHz, and no bitrate buys
 * that back. Opus picks its own bandwidth for the rate it is given, so handing it
 * the full-rate signal and a real bitrate is both simpler and better; the graph
 * that did the resampling is gone rather than reconfigured, and with it a start
 * that could come up silent if the context was suspended.
 */
export async function startVoice(): Promise<Capture> {
  const mime = pickMime(VOICE_MIMES);
  if (!mime) throw new Error('Браузер не умеет записывать звук');

  const devices: Devices = { tracks: [] };
  try {
    const microphone = await openMicrophone();
    devices.tracks.push(...microphone.getTracks());

    // What is encoded is the turned-up signal, not the microphone as it came;
    // see amplify for why the browser is no longer doing that for us.
    const amplified = await amplify(microphone);
    devices.audio = amplified.close;
    // The microphone itself if there was nothing to amplify — a stream with no
    // audio track in it is not something getUserMedia hands back for an audio
    // request, but recording is not the place to find out that it did.
    const recorded = amplified.track ? new MediaStream([amplified.track]) : microphone;

    const recorder = new MediaRecorder(recorded, {
      mimeType: mime,
      audioBitsPerSecond: audioBitsPerSecond(),
    });
    const collect = collector(recorder, mime);
    const startedAt = performance.now();
    recorder.start();

    return {
      mode: 'voice',
      preview: null,
      useCamera: null,
      setZoom: null,
      async stop() {
        await tail(recorder);
        const elapsed = performance.now() - startedAt;
        const blob = await collect();
        release(devices);
        return {
          mode: 'voice',
          blob,
          mime: baseMime(mime),
          durationMs: Math.round(elapsed),
          poster: null,
        };
      },
      cancel() {
        if (recorder.state !== 'inactive') recorder.stop();
        release(devices);
      },
    };
  } catch (error) {
    release(devices);
    throw error;
  }
}

/**
 * A take, by whichever route this device has.
 *
 * The кружочек is the one that forks, and only on iOS. The import is dynamic so
 * that the web bundle never pulls the Capacitor bridge in — the same reason
 * lib/useNativeBack.ts imports @capacitor/app the way it does — and because the
 * flag it is guarded by is false everywhere else, the chunk is never fetched
 * either. A voice message is a voice message on every platform: nothing about
 * an audio-only MediaRecorder has ever frozen.
 */
export async function startCapture(
  mode: RecordMode,
  onLimit?: () => void,
  zoom?: number,
): Promise<Capture> {
  if (mode !== 'video') return startVoice();
  if (!hasNativeVideoNote) return startVideoNote(onLimit, zoom);
  const { startNativeVideoNote } = await import('./nativeVideoNote.ts');
  // Both paths are handed the framing the pinch chose, and both open their own
  // camera to apply it to: a MediaStreamTrack constraint on one side, an
  // AVCaptureDevice's videoZoomFactor on the other.
  return startNativeVideoNote(onLimit, zoom);
}

/**
 * A clip filmed on the camera screen: the camera as it is, plus sound.
 *
 * The one thing this deliberately does not do is what startVideoNote spends most
 * of its length on. There is no canvas, no crop and no downscale, because a
 * кружочек is square and small by design and this is an ordinary video that will
 * sit in an album tile beside picked ones. What MediaRecorder is handed is the
 * camera's own track.
 *
 * That track is the caller's, not this function's: the camera screen has it open
 * and on screen before the shutter is ever pressed, and it goes on showing the
 * viewfinder afterwards. Opening a second one here would blink the picture at the
 * start of every take and hold two cameras that phones will not both give. So
 * nothing here stops it.
 *
 * The microphone is the caller's for the same reason and a sharper one. This used
 * to open it itself, which put a permission prompt directly in front of the
 * shutter: the first seconds of the take went to a dialog, and answering it late
 * meant filming silently. The screen opens it when the mode is switched to video
 * instead — intent stated, and stated early — and holds it across takes, so the
 * second clip asks nothing at all. Passing `null` is filming without sound on
 * purpose; a refused microphone is not a refused take, since a clip is a picture
 * first and losing one that has already been filmed over a permission is worse
 * than a silent file the screen warns about.
 *
 * What each take does own is the gain stage in front of that microphone, which is
 * built and torn down per recording; `stop` and `cancel` close the graph and
 * leave the device alone.
 *
 * The bitrate is the *import* budget rather than the recording one, and that is
 * the whole reason the clip needs no second pass: wouldShrink compares a file
 * against exactly this number, so a clip recorded at it is left alone by
 * compressAlbumVideos and uploaded as filmed. The frame is asked down to the
 * import size at the same time, since 1080p is what the viewfinder wants for a
 * photograph and more than this bitrate can carry.
 */
export async function startClip(
  video: MediaStreamTrack,
  microphone: MediaStream | null,
  maxMs: number,
  onLimit?: () => void,
): Promise<ClipTake> {
  const mime = pickMime(VIDEO_MIMES);
  if (!mime) throw new Error('Браузер не умеет записывать видео');

  const devices: Devices = { tracks: [] };
  try {
    // Nothing is pushed onto devices.tracks: neither device belongs to the take.
    let audio: MediaStreamTrack | undefined;
    if (microphone) {
      const amplified = await amplify(microphone);
      devices.audio = amplified.close;
      audio = amplified.track;
    }

    // Restored when the take ends, so going back to photographs goes back to the
    // full frame. Both calls are allowed to fail: a camera that will not change
    // mode mid-stream is a clip at the size it already was, not a lost take.
    const { importPx, importKbps, importAudioKbps } = importQuality();
    const framing = video.getConstraints();
    try {
      await video.applyConstraints({ ...framing, width: { ideal: importPx } });
    } catch (error) {
      console.warn('capture: filming at the size the viewfinder already had', error);
    }
    const reframe = () => {
      void video.applyConstraints(framing).catch(() => {});
    };

    const recorder = new MediaRecorder(new MediaStream(audio ? [video, audio] : [video]), {
      mimeType: mime,
      videoBitsPerSecond: importKbps * 1000,
      audioBitsPerSecond: importAudioKbps * 1000,
    });
    const collect = collector(recorder, mime);
    const startedAt = performance.now();
    recorder.start();

    const limit = window.setTimeout(() => {
      if (recorder.state !== 'inactive') recorder.stop();
      onLimit?.();
    }, maxMs);

    return {
      sound: audio !== undefined,
      async stop() {
        window.clearTimeout(limit);
        await tail(recorder);
        const elapsed = performance.now() - startedAt;
        const blob = await collect();
        release(devices);
        reframe();
        return { blob, mime: baseMime(mime), durationMs: Math.min(Math.round(elapsed), maxMs) };
      },
      cancel() {
        window.clearTimeout(limit);
        if (recorder.state !== 'inactive') recorder.stop();
        release(devices);
        reframe();
      },
    };
  } catch (error) {
    release(devices);
    throw error;
  }
}

/**
 * Why a take could not start, in the words the composer shows. Everything here
 * is a fact about the device or the page, not about the recording.
 */
export function captureError(error: unknown): string {
  const name = error instanceof DOMException ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Нет доступа к камере и микрофону';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'Камера или микрофон не найдены';
  }
  if (name === 'NotReadableError') return 'Камера занята другим приложением';
  if (!window.isSecureContext) return 'Запись работает только по HTTPS';
  return error instanceof Error && error.message ? error.message : 'Не удалось начать запись';
}
